/**
 * Per-polyp finding vocabulary — `BACKLOG.md` M5: "per-polyp count/size/location/Paris classification
 * linked to pathology." Four closed value sets, each open-but-checked at the application boundary
 * (`gastroenterologyRequests.ts`), matching `dermatology/patchTest.ts`'s own convention for a small,
 * fixed, clinically-standard set.
 *
 * The Paris classification and colon-segment vocabulary are fixed nomenclature (naming conventions,
 * not numeric thresholds), so — unlike `bowelPrep.ts`/`surveillanceIntervals.ts` — nothing here carries
 * the UNREVIEWED caveat. `HISTOLOGY_TYPES`/`DYSPLASIA_GRADES` are the standard categories a pathology
 * report returns; still worth an independent clinical read before this ships for real, since a
 * miscategorized histology feeds directly into `surveillanceIntervals.ts`'s own UNREVIEWED interval
 * table.
 */

/** The eight colon segments/landmarks a polyp's location is documented against, cecum to rectum,
 * matching standard colonoscopy reporting convention. */
export const COLON_SEGMENTS = [
  'cecum',
  'ascending_colon',
  'hepatic_flexure',
  'transverse_colon',
  'splenic_flexure',
  'descending_colon',
  'sigmoid_colon',
  'rectum',
] as const;
export type ColonSegment = (typeof COLON_SEGMENTS)[number];

/**
 * Paris classification for superficial neoplastic lesions — protruded (0-Ip pedunculated, 0-Is
 * sessile), flat/elevated (0-IIa/0-IIb/0-IIc), and excavated (0-III). Standard endoscopic nomenclature,
 * not a clinical threshold.
 */
export const PARIS_CLASSIFICATIONS = ['0-Ip', '0-Is', '0-IIa', '0-IIb', '0-IIc', '0-III'] as const;
export type ParisClassification = (typeof PARIS_CLASSIFICATIONS)[number];

export const REMOVAL_METHODS = [
  'cold_forceps',
  'cold_snare',
  'hot_snare',
  'emr',
  'not_removed',
] as const;
export type RemovalMethod = (typeof REMOVAL_METHODS)[number];

/** `pending` until a pathology result is recorded (`polypService.ts`'s `recordPolypPathology`);
 * `resulted` once histology/dysplasia are on file. A polyp created at procedure time is always
 * `pending` — pathology is never known in the room. */
export const PATHOLOGY_STATUSES = ['pending', 'resulted'] as const;
export type PathologyStatus = (typeof PATHOLOGY_STATUSES)[number];

export const HISTOLOGY_TYPES = [
  'tubular_adenoma',
  'tubulovillous_adenoma',
  'villous_adenoma',
  'sessile_serrated_lesion',
  'traditional_serrated_adenoma',
  'hyperplastic_polyp',
  'adenocarcinoma',
  'normal_mucosa',
  'other',
] as const;
export type HistologyType = (typeof HISTOLOGY_TYPES)[number];

export const DYSPLASIA_GRADES = ['low_grade', 'high_grade'] as const;
export type DysplasiaGrade = (typeof DYSPLASIA_GRADES)[number];

/** The histology types `adrCalculation.ts`/`surveillanceIntervals.ts` treat as a real adenoma finding
 * for ADR-numerator and surveillance-interval purposes — conventional adenoma histology, not the
 * serrated pathway (`isSerratedHistology` below) or a non-neoplastic/malignant finding. */
export function isAdenomaHistology(histology: HistologyType): boolean {
  return (
    histology === 'tubular_adenoma' ||
    histology === 'tubulovillous_adenoma' ||
    histology === 'villous_adenoma'
  );
}

export function isSerratedHistology(histology: HistologyType): boolean {
  return histology === 'sessile_serrated_lesion' || histology === 'traditional_serrated_adenoma';
}

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isColonSegment(value: string): value is ColonSegment {
  return includes(COLON_SEGMENTS, value);
}

export function isParisClassification(value: string): value is ParisClassification {
  return includes(PARIS_CLASSIFICATIONS, value);
}

export function isRemovalMethod(value: string): value is RemovalMethod {
  return includes(REMOVAL_METHODS, value);
}

export function isHistologyType(value: string): value is HistologyType {
  return includes(HISTOLOGY_TYPES, value);
}

export function isDysplasiaGrade(value: string): value is DysplasiaGrade {
  return includes(DYSPLASIA_GRADES, value);
}
